import React, { createContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import CenterAlert from "../Components/CenterAlert";

export const CartContext = createContext();

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const API = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
});

const CartProvider = ({ children }) => {
  const getStoredUser = () => {
    try {
      return JSON.parse(localStorage.getItem("user")) || null;
    } catch (error) {
      return null;
    }
  };

  const getToken = () => localStorage.getItem("token");

  const getUserId = () => {
    const user = getStoredUser();
    return user?._id || user?.id || "guest";
  };

  const getCartStorageKey = () => `cartItems_${getUserId()}`;
  const getCouponStorageKey = () => `cartCoupon_${getUserId()}`;
  const guestCartKey = "cartItems_guest";

  const [cartKey, setCartKey] = useState(getCartStorageKey());
  const [cartItems, setCartItems] = useState(() => {
    try {
      const storedCart = localStorage.getItem(getCartStorageKey());
      return storedCart ? JSON.parse(storedCart) : [];
    } catch (error) {
      return [];
    }
  });

  const [isInitialized, setIsInitialized] = useState(false);

  const [couponCode, setCouponCode] = useState("");
  const [discount, setDiscount] = useState(0);
  const [couponMessage, setCouponMessage] = useState("");
  const [couponApplied, setCouponApplied] = useState(false);
  const [couponLoading, setCouponLoading] = useState(false);

  const [alertConfig, setAlertConfig] = useState({
    isOpen: false,
    type: "success",
    title: "",
    message: "",
  });

  const triggerAlert = (type, title, message) => {
    setAlertConfig({ isOpen: true, type, title, message });
  };

  const closeAlert = () => {
    setAlertConfig((prev) => ({ ...prev, isOpen: false }));
  };

  const isAuthenticated = () => !!getToken();

  const getItemId = (item) =>
    item?._id || item?.id || item?.product?._id || item?.productId?._id || item?.productId;

  const authHeaders = () => ({
    headers: {
      Authorization: `Bearer ${getToken()}`,
    },
  });

  const persistCouponState = ({
    nextCouponCode = couponCode,
    nextDiscount = discount,
    nextCouponMessage = couponMessage,
    nextCouponApplied = couponApplied,
  } = {}) => {
    try {
      localStorage.setItem(
        getCouponStorageKey(),
        JSON.stringify({
          couponCode: nextCouponCode,
          discount: nextDiscount,
          couponMessage: nextCouponMessage,
          couponApplied: nextCouponApplied,
        })
      );
    } catch (error) {}
  };

  const loadCouponState = () => {
    try {
      const savedCoupon = JSON.parse(
        localStorage.getItem(getCouponStorageKey()) || "null"
      );

      if (savedCoupon && savedCoupon.couponApplied) {
        setCouponCode(savedCoupon.couponCode || "");
        setDiscount(Number(savedCoupon.discount || 0));
        setCouponMessage(savedCoupon.couponMessage || "");
        setCouponApplied(true);
      } else {
        setCouponCode("");
        setDiscount(0);
        setCouponMessage("");
        setCouponApplied(false);
      }
    } catch (error) {
      setCouponCode("");
      setDiscount(0);
      setCouponMessage("");
      setCouponApplied(false);
    }
  };

  const removeCoupon = (showToast = false) => {
    setCouponCode("");
    setDiscount(0);
    setCouponMessage("");
    setCouponApplied(false);
    setCouponLoading(false);

    try {
      localStorage.removeItem(getCouponStorageKey());
    } catch (error) {}

    if (showToast) {
      toast.success("Coupon removed");
    }
  };

  const normalizeCartItems = (data) => {
    const rawItems =
      data?.items ||
      data?.cart?.items ||
      data?.data?.items ||
      (Array.isArray(data) ? data : []) ||
      [];

    if (!Array.isArray(rawItems)) return [];

    return rawItems
      .map((item) => {
        const product =
          item?.product && typeof item.product === "object"
            ? item.product
            : item?.productId && typeof item.productId === "object"
            ? item.productId
            : null;

        if (!product?._id) return null;

        return {
          ...item,
          _id: item._id || product._id, // Use unique item _id if available from backend
          product,
          productId: product._id,
          name: item.name || product.name || "",
          image: product.image || item.image || "",
          price: Number(product.price ?? item.price ?? 0),
          quantity: Math.max(1, Number(item.quantity ?? 1)),
          category: product.category || item.category || "", 
        };
      })
      .filter(Boolean);
  };

  const sanitizeLocalCartItems = (items = []) => {
    if (!Array.isArray(items)) return [];

    return items
      .map((item) => {
        const id = getItemId(item);
        if (!id) return null;

        return {
          ...item,
          _id: item._id || id, // Preserve unique local _id for custom items
          productId: id,
          quantity: Math.max(1, Number(item.quantity ?? 1)),
          price: Number(item.price ?? 0),
          name: item.name || "",
          image: item.image || "",
          category: item.category || item.product?.category || "", 
        };
      })
      .filter(Boolean);
  };

  const totalItems = useMemo(() => {
    return cartItems.reduce((acc, item) => acc + Number(item.quantity || 1), 0);
  }, [cartItems]);

  const totalPrice = useMemo(() => {
    return cartItems.reduce(
      (acc, item) => acc + Number(item.price || 0) * Number(item.quantity || 1),
      0
    );
  }, [cartItems]);

  const shippingFee = useMemo(() => {
    return totalPrice >= 499 || totalPrice === 0 ? 0 : 59;
  }, [totalPrice]);

  const finalTotal = useMemo(() => {
    return Math.max(0, Number(totalPrice || 0) + Number(shippingFee || 0) - Number(discount || 0));
  }, [totalPrice, shippingFee, discount]);

  useEffect(() => {
    if (!isInitialized || !couponApplied || !couponCode.trim()) return;

    if (totalPrice <= 0) {
      removeCoupon(false);
      return;
    }

    const revalidateCoupon = async () => {
      try {
        const res = await API.post("/api/coupons/apply", {
          code: couponCode.trim().toUpperCase(),
          cartTotal: totalPrice,
        });

        const newDiscount = Number(res.data?.discount || 0);

        if (newDiscount <= 0) {
          removeCoupon(false);
          toast.error("Coupon removed: minimum cart amount not met");
          return;
        }

        const updatedMessage = `Coupon applied successfully! Saved ₹${newDiscount}`;

        setDiscount(newDiscount);
        setCouponMessage(updatedMessage);

        persistCouponState({
          nextCouponCode: couponCode.trim().toUpperCase(),
          nextDiscount: newDiscount,
          nextCouponMessage: updatedMessage,
          nextCouponApplied: true,
        });
      } catch (error) {
        const errorMessage =
          error.response?.data?.message || "Coupon removed: minimum amount not met";
        removeCoupon(false);
        toast.error(errorMessage);
      }
    };

    revalidateCoupon();
  }, [totalPrice, isInitialized, couponApplied, couponCode]);

  const validateGuestCart = async (items) => {
    try {
      const cleanItems = sanitizeLocalCartItems(items);
      if (!cleanItems.length) return [];

      const res = await API.post("/api/cart/validate", {
        items: cleanItems.map((item) => ({
          productId: item.productId,
          quantity: Number(item.quantity || 1),
        })),
      });

      // We merge validated data but preserve local names/customizations
      const validatedData = normalizeCartItems(res.data);
      return cleanItems.map(localItem => {
        const validated = validatedData.find(v => v.productId === localItem.productId);
        return validated ? { ...validated, _id: localItem._id, name: localItem.name } : localItem;
      });

    } catch (error) {
      return [];
    }
  };

  const loadBackendCart = async (showErrorToast = false) => {
    try {
      const res = await API.get("/api/cart", authHeaders());
      const normalized = normalizeCartItems(res.data);
      setCartItems(normalized);
      return normalized;
    } catch (error) {
      setCartItems([]);
      if (showErrorToast) {
        toast.error(error.response?.data?.message || "Failed to load cart");
      }
      return [];
    }
  };

  const mergeGuestCartToBackend = async () => {
    try {
      const guestCart = JSON.parse(localStorage.getItem(guestCartKey) || "[]");
      const cleanGuestCart = sanitizeLocalCartItems(guestCart);

      if (!cleanGuestCart.length || !isAuthenticated()) return;

      await API.post(
        "/api/cart/merge",
        {
          items: cleanGuestCart.map((item) => ({
            productId: item.productId,
            quantity: Number(item.quantity || 1),
            name: item.name // Pass customized names during merge
          })),
        },
        authHeaders()
      );

      localStorage.removeItem(guestCartKey);
    } catch (error) {}
  };

  const applyCoupon = async () => {
    if (!couponCode.trim()) {
      setCouponMessage("Please enter a coupon code");
      setCouponApplied(false);
      return;
    }

    if (totalPrice <= 0) {
      setCouponMessage("Your cart is empty");
      setCouponApplied(false);
      return;
    }

    try {
      setCouponLoading(true);
      setCouponMessage("");

      const res = await API.post("/api/coupons/apply", {
        code: couponCode.trim().toUpperCase(),
        cartTotal: totalPrice,
      });

      const discountAmount = Number(res.data?.discount || 0);
      const nextMessage =
        discountAmount > 0
          ? `Coupon applied successfully! Saved ₹${discountAmount}`
          : "Coupon applied successfully";

      const formattedCode = couponCode.trim().toUpperCase();

      setCouponCode(formattedCode);
      setDiscount(discountAmount);
      setCouponMessage(nextMessage);
      setCouponApplied(true);

      persistCouponState({
        nextCouponCode: formattedCode,
        nextDiscount: discountAmount,
        nextCouponMessage: nextMessage,
        nextCouponApplied: true,
      });

      toast.success("Coupon applied");
    } catch (error) {
      const message = error.response?.data?.message || "Invalid coupon";
      setDiscount(0);
      setCouponApplied(false);
      setCouponMessage(message);

      persistCouponState({
        nextCouponCode: couponCode.trim().toUpperCase(),
        nextDiscount: 0,
        nextCouponMessage: message,
        nextCouponApplied: false,
      });
    } finally {
      setCouponLoading(false);
    }
  };

  useEffect(() => {
    const syncCartWithUser = async () => {
      const newKey = getCartStorageKey();
      setCartKey(newKey);

      if (isAuthenticated()) {
        await mergeGuestCartToBackend();
        await loadBackendCart();
      } else {
        try {
          const storedCart = JSON.parse(localStorage.getItem(newKey) || "[]");
          const validCart = await validateGuestCart(storedCart);
          setCartItems(validCart);
          localStorage.setItem(newKey, JSON.stringify(validCart));
        } catch (error) {
          setCartItems([]);
          localStorage.setItem(newKey, JSON.stringify([]));
        }
      }

      loadCouponState();
      setIsInitialized(true);
    };

    syncCartWithUser();

    window.addEventListener("storage", syncCartWithUser);
    return () => {
      window.removeEventListener("storage", syncCartWithUser);
    };
  }, []);

  useEffect(() => {
    if (!isAuthenticated()) {
      localStorage.setItem(cartKey, JSON.stringify(cartItems));
    }
  }, [cartItems, cartKey]);

  useEffect(() => {
    if (!isInitialized) return;

    if (cartItems.length === 0) {
      removeCoupon(false);
      return;
    }

    if (couponApplied) {
      persistCouponState();
    }
  }, [cartItems, couponApplied, isInitialized]);

  // ==========================================
  // 🛒 ADD TO CART (WITH CUSTOM TEXT LOGIC)
  // ==========================================
  const addToCart = async (product) => {
    const productId = getItemId(product);
    const qty = Math.max(1, Number(product.quantity || 1));

    // 🎯 Extract custom text and build the final product name
    const customText = product.customText || "";
    const finalName = customText ? `${product.name} (Name: ${customText})` : (product.name || "");

    if (!productId) {
      triggerAlert("error", "Error", "Invalid product selected.");
      return;
    }

    if (isAuthenticated()) {
      try {
        const res = await API.post(
          "/api/cart/add",
          {
            productId,
            quantity: qty,
            customText, // 🎯 Send customization to backend
            name: finalName // Fallback name
          },
          authHeaders()
        );

        setCartItems(normalizeCartItems(res.data));
        triggerAlert("success", "Added to Cart!", `${finalName} has been added to your shopping cart.`);
      } catch (error) {
        triggerAlert("error", "Failed to Add", error.response?.data?.message || "Failed to add item to cart.");
      }
      return;
    }

    // 🎯 GUEST CART LOGIC
    // We match against BOTH productId AND finalName so different customizations are treated as separate items
    const existing = cartItems.find((item) => item.productId === productId && item.name === finalName);

    if (existing) {
      const updatedCart = cartItems.map((item) =>
        item.productId === productId && item.name === finalName
          ? {
              ...item,
              quantity: Number(item.quantity || 1) + qty,
            }
          : item
      );

      const validCart = await validateGuestCart(updatedCart);
      setCartItems(validCart);
      triggerAlert("success", "Cart Updated", `${finalName} quantity has been updated.`);
    } else {
      const updatedCart = [
        ...cartItems,
        {
          _id: customText ? `${productId}_${Date.now()}` : productId, // Unique local ID for custom items
          productId,
          name: finalName, // 🎯 Save the customized name
          image: product.image || "",
          price: Number(product.price || 0),
          quantity: qty,
          category: product.category || "", 
        },
      ];

      const validCart = await validateGuestCart(updatedCart);
      setCartItems(validCart);
      triggerAlert("success", "Added to Cart!", `${finalName} has been added to your shopping cart.`);
    }
  };

  const removeFromCart = async (id) => {
    const removedProduct = cartItems.find((item) => getItemId(item) === id);

    if (isAuthenticated()) {
      try {
        const res = await API.delete(`/api/cart/remove/${id}`, authHeaders());
        setCartItems(normalizeCartItems(res.data));
        if (removedProduct) {
          triggerAlert("success", "Removed from Cart", `${removedProduct.name} has been removed.`);
        }
      } catch (error) {
        toast.error(error.response?.data?.message || "Failed to remove item");
      }
      return;
    }

    const updatedCart = cartItems.filter((item) => getItemId(item) !== id);
    setCartItems(updatedCart);

    if (removedProduct) {
      triggerAlert("success", "Removed from Cart", `${removedProduct.name} has been removed.`);
    }
  };

  const increaseQty = async (id) => {
    const item = cartItems.find((cartItem) => getItemId(cartItem) === id);
    if (!item) return;

    const newQty = Number(item.quantity || 1) + 1;

    if (isAuthenticated()) {
      try {
        const res = await API.put(
          `/api/cart/update/${id}`,
          { quantity: newQty },
          authHeaders()
        );
        setCartItems(normalizeCartItems(res.data));
      } catch (error) {
        toast.error(error.response?.data?.message || "Failed to update quantity");
      }
      return;
    }

    const updatedCart = cartItems.map((cartItem) =>
      getItemId(cartItem) === id ? { ...cartItem, quantity: newQty } : cartItem
    );

    const validCart = await validateGuestCart(updatedCart);
    setCartItems(validCart);
  };

  const decreaseQty = async (id) => {
    const item = cartItems.find((cartItem) => getItemId(cartItem) === id);
    if (!item) return;

    const currentQty = Number(item.quantity || 1);
    const newQty = currentQty > 1 ? currentQty - 1 : 1;

    if (isAuthenticated()) {
      try {
        const res = await API.put(
          `/api/cart/update/${id}`,
          { quantity: newQty },
          authHeaders()
        );
        setCartItems(normalizeCartItems(res.data));
      } catch (error) {
        toast.error(error.response?.data?.message || "Failed to update quantity");
      }
      return;
    }

    const updatedCart = cartItems.map((cartItem) =>
      getItemId(cartItem) === id ? { ...cartItem, quantity: newQty } : cartItem
    );

    const validCart = await validateGuestCart(updatedCart);
    setCartItems(validCart);
  };

  const clearCartSilently = async () => {
    setCartItems([]);
    removeCoupon(false);

    try {
      localStorage.setItem(cartKey, JSON.stringify([]));
      localStorage.removeItem(guestCartKey);
    } catch (error) {}

    if (isAuthenticated()) {
      try {
        await API.delete("/api/cart/clear", authHeaders());
      } catch (error) {}
    }
  };

  const clearCart = async () => {
    if (isAuthenticated()) {
      try {
        await API.delete("/api/cart/clear", authHeaders());
        setCartItems([]);
        removeCoupon();
        triggerAlert("success", "Cart Cleared", "Your cart has been successfully emptied.");
      } catch (error) {
        triggerAlert("error", "Error", error.response?.data?.message || "Failed to clear cart");
      }
      return;
    }

    setCartItems([]);
    localStorage.setItem(cartKey, JSON.stringify([]));
    removeCoupon();
    triggerAlert("success", "Cart Cleared", "Your cart has been successfully emptied.");
  };

  return (
    <CartContext.Provider
      value={{
        cartItems,
        setCartItems,
        addToCart,
        removeFromCart,
        increaseQty,
        decreaseQty,
        clearCart,
        clearCartSilently,
        totalItems,
        totalPrice,
        shippingFee,
        finalTotal,
        loadBackendCart,
        couponCode,
        setCouponCode,
        discount,
        couponMessage,
        couponApplied,
        couponLoading,
        applyCoupon,
        removeCoupon,
      }}
    >
      <CenterAlert {...alertConfig} onClose={closeAlert} />
      {children}
    </CartContext.Provider>
  );
};

export default CartProvider;